<template>
  <SubLayout
      :pageTitle="title"
      :pageBreadcrumbs="breadcrumbs"
  >
    <template #layoutContent>
      <Loader :isLoading="isLoading">
        <InvoiceForm
            :invoice="invoice"
            :submit-form="!isEdit ? createInvoice : updateInvoice"
            :isPaid="isPaid"
            :submitting="submitting" />
      </Loader>
    </template>
  </SubLayout>
</template>

<script setup>

import SubLayout from "@/views/Layouts/partials/SubLayout.vue";
import {computed, onMounted, ref, watch} from "vue";
import InvoiceForm from "@/components/recurrent/InvoiceForm.vue";
import {useStore} from "vuex";
import {useRoute, useRouter} from "vue-router";
import {formatDate} from "@/mixins/Helpers";
import Loader from "@/components/Loader";

const route = useRoute()
const router = useRouter()
const store = useStore()
const title = ref("breadcrumbs.recurrent.title")
const breadcrumbs = ref([
  {
    name: 'breadcrumbs.recurrent.list',
    route: 'recurrent-invoices',
    params: {}
  },
  {
    name: route.params.id ? 'breadcrumbs.recurrent.edit' : 'breadcrumbs.recurrent.new',
    route: route.params.id ? 'recurrent-invoices-edit' : 'recurrent-invoices-create',
    params: route.params.id ? { id: route.params.id } : null
  }
])

const isLoading = ref(true)
const submitting = ref(false)

const today = new Date();
const day = String(today.getDate()).padStart(2, '0');
const month = String(today.getMonth() + 1).padStart(2, '0');
const year = today.getFullYear();
const formattedStartDate = `${day}-${month}-${year}`;

const invoice = ref({
  id: null,
  invoice_date: formattedStartDate,
  items: [{
    product_id: '',
    quantity: 1,
    price: 0,
    description: '',
    discount_type: 'percent',
    discount_value: '',
    taxes: [],
    total: 0
  }],
  taxes: [],
  invoice_discount_type:  'percent',
  invoice_discount_value:  0,
  paymentStatus: '',
  payment_condition_id: '',
  exchange_rate: 1,
  opportunity_id: '',
  payment_type_id: '',
  recurring_date: null,
  frequency: '',
  limit_by: 'none',
  limit_count: null,
  frequency_count: null,
  limit_date: null,
})

const isEdit = computed(() => !!route.params.id)
const currentInvoice = computed(() => store.getters['invoices/currentRecurrentInvoice'])
const paymentStatus = computed(() => store.getters['payments/paymentStatus'])
const paymentPaidStatus = computed(() => paymentStatus.value.find(el => el.value == 'PAID') || '')
const invoiceStatus = computed(() => paymentStatus.value.find(el => el.value == invoice.value.paymentStatus) || '')
const contacts = computed(() => store.getters['contacts/getContacts'])

const isPaid = computed(() => invoiceStatus.value === paymentPaidStatus.value)

watch(() => currentInvoice.value, (newValue) => {
  if(newValue.id && route.params.id) {
    invoice.value.id = newValue.id
    invoice.value.invoice_date = formatDate(newValue.invoice_date)
    invoice.value.items = newValue.items?.map(el => ({
      product_id: el.product_sale_element_id,
      quantity: el.quantity,
      price: el.price,
      description: el.description,
      discount_type: el.discount_type,
      discount_value: el.discount_value ? el.discount_value : null,
      taxes: el.taxes?.map(el => el.id),
    }))
    invoice.value.taxes = []
    invoice.value.taxes = newValue.taxes?.map(el => el.taxe_id)
    invoice.value.invoice_discount_type =  newValue.invoice_discount_type
    invoice.value.invoice_discount_value =  newValue.invoice_discount_value ? newValue.invoice_discount_value : null
    invoice.value.exchange_rate = newValue.exchange_rate
    invoice.value.currency_id =  newValue.currency.id
    invoice.value.contact_id = newValue.contact_id
    invoice.value.due_date =  formatDate(newValue.due_date)
    invoice.value.notes = newValue.notes ||  ""
    invoice.value.paymentStatus = newValue.payment_status
    invoice.value.payment_condition_id = newValue.payment_condition_id
    invoice.value.opportunity_id = newValue.opportunity_id
    invoice.value.payment_type_id = newValue?.paymentType?.id
    invoice.value.frequency = newValue.frequency
    invoice.value.frequency_count = newValue.frequency_count
    invoice.value.recurring_date =  formatDate(newValue.recurring_date)
    invoice.value.limit_date = formatDate(newValue.limit_date)
    invoice.value.limit_by = newValue.limit_by
    invoice.value.limit_count = newValue.limit_count

  } else {
    invoice.value = null
    invoice.value = {
      invoice_date: formattedStartDate,
      items: [{
        product_id: '',
        quantity: 1,
        price: 0,
        description: '',
        discount_type: 'percent',
        discount_value: '',
        taxes: [],
        total: 0
      }],
      taxes: [],
      invoice_discount_type:  'percent',
      invoice_discount_value:  0,
      notes: '',
      paymentStatus: '',
      payment_condition_id: '',
      exchange_rate: 1,
      opportunity_id: '',
      payment_type_id: '',
    }
  }

}, {immediate: true})

const createInvoice = () => {
  submitting.value = true
  store.dispatch('invoices/createRecurrentInvoice', invoice.value)
      .then((response) => {
        window.toastr['success'](i18n.global.t('notification.invoice_created'))
        router.push({ name: 'recurrent-invoices' })
      })
      .catch(err => {
      }).finally(() => submitting.value = false)
}

const updateInvoice = () => {
  submitting.value = true
  store.dispatch('invoices/updateRecurrentInvoice', invoice.value)
      .then((response) => {
        window.toastr['success'](i18n.global.t('notification.invoice_updated'))
        router.push({ name: 'recurrent-invoices' })
      })
      .catch(err => {
      }).finally(() => submitting.value = false)
}

const loadCurrentInvoice = async () => {
  if(route.params.id){
    await store.dispatch('invoices/getRecurrentInvoiceById', route.params.id) .then((response) => {
      invoice.value.payment_condition_id = response.data.data?.paymentCondition.id
    })
  }
}
function loadData() {
  Promise.all([
      store.dispatch('contacts/loadContacts'),
      store.dispatch('taxes/loadTaxes'),
      store.dispatch('products/loadAllProductSale'),
      store.dispatch('currencies/loadCurrencies'),
      store.dispatch('payments/loadPaymentStatus'),
      store.dispatch('company/generalInformation'),
      store.dispatch('payments/loadPaymentConditions'),
      store.dispatch('payments/loadPaymentTypes'),
      loadCurrentInvoice()
  ]).finally(() => {
    if(route.query.opportunity_id){
      let products = JSON.parse(route.query.products)
      if(products.length !== 0){
        invoice.value.items = products?.map(el => {
          return {
            product_id: el.id,
            quantity: el.quantity,
            price: el.price,
            description: '',
            discount_type: '',
            discount_value: 0,
            taxes: el.taxes,
          }
        })
      }

      invoice.value.contact_id = route.query.contact_id
      invoice.value.opportunity_id =  route.query.opportunity_id
      let selectedContact = contacts.value.find(contact => contact.id === route.query.contact_id);
      if (selectedContact && selectedContact.currency_id) {
        invoice.value.currency_id = selectedContact.currency_id;
      }
    }
    isLoading.value = false
  })
}

onMounted(() => {
  loadData()
})

</script>

<style scoped>

</style>
